import React, { useEffect, useRef, useState } from 'react';
import { IoArrowBack } from 'react-icons/io5';
import { useNavigate, useParams } from 'react-router-dom';
import { getLangName } from '../../components/helper';
import CustomButton from '../../components/button/button';
import successToast from '../../components/toasts/successToast';
import errorToast from '../../components/toasts/errorToast';
import { useDispatch } from 'react-redux';
import { sendToModelAction } from '../../redux/actions/translationAction';
import { Spinner } from 'react-bootstrap';
import useApiFetch from '../../hooks/useApiFetch';
import { OKAPI_BASE_URL } from '../../redux/config';

const DocEditSummary = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { id } = useParams();

    const docsHook = useApiFetch(OKAPI_BASE_URL);

    const [page, setPage] = useState(1);
    const [hasNext, setHasNext] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [jobData, setJobData] = useState(null);
    const [segments, setSegments] = useState([]);

    // Single scroll container
    const scrollContainerRef = useRef(null);
    // Extra guard to prevent duplicate / parallel fetches
    const isFetchingRef = useRef(false);

    // Fetch a specific page
    const fetchPage = async (pg, append = false) => {
        // if we are already fetching, don't start another
        if (isFetchingRef.current) return;

        // If we are trying to append but server already said no more
        if (append && !hasNext) return;

        isFetchingRef.current = true;
        if (append) setLoadingMore(true);

        try {
            const json = await docsHook.fetchData(
                `/document/${id}/mod-summary`,
                pg,
                10
            );

            const mods = json.mod_segments || [];

            const normalized = mods.map((seg) => ({
                source: seg?.source?.text || '',
                edited: seg?.target?.text || '',
                src_lang: seg.src_lang,
                trg_lang: seg.trg_lang,
            }));

            if (!append) {
                // First / fresh load
                if (normalized.length > 0) {
                    setJobData({
                        source_lang: normalized[0].src_lang,
                        target_lang: normalized[0].trg_lang,
                    });
                } else {
                    setJobData(null);
                }
                setSegments(normalized);
            } else {
                // Append for next pages
                setSegments((prev) => [...prev, ...normalized]);
            }

            setHasNext(json.pagination?.has_next ?? false);
            setPage(pg);
        } catch (err) {
            console.error('Error loading page:', err);
            // you can show a toast here if needed
        } finally {
            isFetchingRef.current = false;
            if (append) setLoadingMore(false);
        }
    };

    // Initial load / when id changes
    useEffect(() => {
        setPage(1);
        setSegments([]);
        setHasNext(true);
        setLoadingMore(false);
        setJobData(null);
        isFetchingRef.current = false;

        fetchPage(1, false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // Scroll handler for infinite scroll
    const handleScroll = (e) => {
        const { scrollTop, clientHeight, scrollHeight } = e.target;

        // When user is near bottom (e.g., within 80px), load next
        const isNearBottom = scrollHeight - scrollTop - clientHeight < 80;

        if (
            isNearBottom &&
            hasNext &&
            !loadingMore &&
            !docsHook.loading &&
            !isFetchingRef.current
        ) {
            const nextPage = page + 1;
            fetchPage(nextPage, true);
        }
    };

    // Send to model
    const handleSend = () => {
        dispatch(
            sendToModelAction({ job_id: id }, onSaveSuccess, onError)
        );
    };

    const onSaveSuccess = async (data) => {
        successToast(data.data.message);
        // await docsHook.putData(`/document/${id}/mod-summary/clear`,)
        navigate('/post-editing');
    };

    const onError = (data) => {
        errorToast(data?.data?.message);
    };

    return (
        <div className="min-vh-100 d-flex flex-column h-100 bg-white text-start">
            {/* Header */}
            <div className="w-100 d-flex justify-content-between align-items-center header border-bottom position-sticky top-0 bg-white py-2">
                <div className="d-flex">
                    <span
                        className="px-3 fs_16 cursor_pointer pb-1"
                        onClick={() => navigate(-1)}
                    >
                        <IoArrowBack />
                    </span>
                    <h2 className="fs_20 fw_400">Review Post Editing</h2>
                </div>

                <div className="d-flex">
                    <CustomButton
                        className="cursor_pointer fs_18 pb-1 mx-1"
                        variant="primary"
                        onClick={() => {
                            successToast('Saved Successfully');
                            navigate('/post-editing');
                        }}
                    >
                        Save Draft
                    </CustomButton>

                    <CustomButton
                        className="cursor_pointer fs_18 pb-1 mx-1"
                        variant="primary"
                        onClick={handleSend}
                    >
                        Send to Model
                    </CustomButton>
                </div>
            </div>

            <div className="container py-2 h-100">
                {docsHook.loading && segments.length === 0 ? (
                    <div
                        className="d-flex justify-content-center align-items-center"
                        style={{ height: '100%' }}
                    >
                        <Spinner animation="border" variant="primary" />
                    </div>
                ) : (
                    <div className="row h-100">
                        <div className="col-12">
                            <div className="rounded h-100 d-flex flex-column border">
                                {/* Header row inside the container */}
                                <div className="p-2 border-bottom bg-light d-flex justify-content-between">
                                    <h5 className="m-0 text-secondary w-100">
                                        Source: {getLangName(jobData?.source_lang)}
                                    </h5>
                                    <h5 className="m-0 text-primary w-100">
                                        Edited: {getLangName(jobData?.target_lang)}
                                    </h5>
                                </div>

                                {/* Single scrollable container for pairs */}
                                <div
                                    className="flex-grow-1 overflow-auto p-2"
                                    ref={scrollContainerRef}
                                    onScroll={handleScroll}
                                    style={{
                                        maxHeight: '80vh',
                                        fontSize: '13px',
                                        lineHeight: '1.7',
                                    }}
                                >
                                    {segments.map((item, idx) => (
                                        <div
                                            key={idx}
                                            className="border rounded mb-2 d-flex"
                                            style={{ gap: "16px" }} // spacing between columns
                                        >
                                            {/* Source (50%) */}
                                            <div className="w-50 p-2 d-flex align-items-center">
                                                <div className="text-secondary">
                                                    {item.source || <em>[Empty]</em>}
                                                </div>
                                            </div>

                                            {/* Vertical Divider */}
                                            <div
                                                style={{
                                                    width: "1px",
                                                    background: "#ddd",
                                                    margin: "0 8px",
                                                }}
                                            />

                                            {/* Edited (50%) */}
                                            <div className="w-50 p-2 d-flex align-items-center">
                                                <div className="text-primary">
                                                    {item.edited || <em>[Empty]</em>}
                                                </div>
                                            </div>
                                        </div>

                                    ))}

                                    {/* Loading more indicator at bottom */}
                                    {loadingMore && (
                                        <div className="text-center py-2">
                                            <Spinner
                                                size="sm"
                                                animation="border"
                                            />
                                        </div>
                                    )}

                                    {/* Optional: end of list text */}
                                    {!hasNext && segments.length > 0 && (
                                        <div className="text-center py-2 text-muted small">
                                            No more segments
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DocEditSummary;
